import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import { DrawioContainer } from "~/components/Document/drawioView";
import { TOAST_MESSAGES } from "~/constants/messages";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

interface DocumentInfo {
  createdAt: string;
  modifiedAt: string;
  modifiedBy?: {
    display_name: string;
  };
}

const WorkFlow: React.FC = () => {
  const { error: projectError } = useProjectMembers();
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const { selectedId } = useParams<{ selectedId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [currentXml, setCurrentXml] = useState<string | undefined>(undefined);
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const documentApi = useMemo(() => new DocumentApi(), []);

  useEffect(() => {
    if (projectError) {
      console.error("Project data loading error:", projectError);
      toast.error(TOAST_MESSAGES.DOCUMENT.PROJECT_LOAD_FAILED);
    }
  }, [projectError]);

  // 業務フロー図を取得
  useEffect(() => {
    if (!currentProject) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    // URLパラメータから取得、なければReduxストアから取得
    const docId = selectedId || currentProject.outputs?.["workflow"]?.id;

    if (!docId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.WORKFLOW_NOT_FOUND);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    const fetchWorkflow = async () => {
      if (!docId) return;

      setDocumentId(docId);

      const response = await documentApi.get(currentUser.organization_id, currentProject.id, docId);

      if (response !== null) {
        // ドキュメントの内容を解析
        const parsedData = JSON.parse(response.content);

        // 旧バージョンのデータ形式（elements/mermaid）をチェック
        if (parsedData.elements || parsedData.mermaid) {
          setHasError(true);
          setIsLoading(false);
          return;
        }

        if (parsedData.xml) {
          setCurrentXml(parsedData.xml);
        }

        // ヘッダー用の情報も同時に設定
        if (response.modified_by) {
          setDocumentInfo({
            createdAt: response.created_at || "",
            modifiedAt: response.modified_at || "",
            modifiedBy: response.modified_by,
          });
        } else {
          setDocumentInfo({
            createdAt: response.created_at || "",
            modifiedAt: response.modified_at || "",
          });
        }

        setIsLoading(false);
      } else {
        toast.error(TOAST_MESSAGES.DOCUMENT.WORKFLOW_FETCH_FAILED);
        setIsLoading(false);
        navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      }
    };
    fetchWorkflow();
  }, [currentProject, currentUser, navigate, documentApi, selectedId]);

  // ドキュメント情報を取得するuseEffectは削除（fetchWorkflowで統合済み）

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  const handleHistoryClick = () => {
    const pathSegments = window.location.pathname.split("/");
    pathSegments.pop();
    navigate(`${pathSegments.join("/")}/history`);
  };

  const handleSave = async (xml: string) => {
    if (!documentId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.SAVE_TARGET_NOT_FOUND);
      return;
    }

    try {
      // XMLをstateに設定
      setCurrentXml(xml);

      // APIを使ってサーバーに保存
      const updateData = {
        content: JSON.stringify({ xml: xml }),
      };

      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        documentId,
        updateData,
      );

      if (!response) {
        toast.error(TOAST_MESSAGES.DOCUMENT.SAVE_FAILED);
        return;
      }

      const processLockApi = new ProcessLockApi();

      const responseProcessLock = await processLockApi.unlock(
        currentUser.organization_id,
        currentProject.id,
        {
          type: "workflow",
          mode: "edit",
        },
      );
      if (responseProcessLock !== null) {
        setCurrentXml(xml);

        const updatedResponse = await documentApi.get(
          currentUser.organization_id,
          currentProject.id,
          documentId,
        );

        if (updatedResponse) {
          if (updatedResponse.modified_by) {
            setDocumentInfo({
              createdAt: updatedResponse.created_at || "",
              modifiedAt: updatedResponse.modified_at || "",
              modifiedBy: updatedResponse.modified_by,
            });
          } else {
            setDocumentInfo({
              createdAt: updatedResponse.created_at || "",
              modifiedAt: updatedResponse.modified_at || "",
            });
          }
        }
      } else {
        toast.error(TOAST_MESSAGES.DOCUMENT.UNLOCK_FAILED);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.DOCUMENT.WORKFLOW_SAVE_FAILED);
      throw error;
    }
  };

  return (
    <div className={styles["workFlow"]}>
      {documentInfo && (
        <CommonDocumentHeader
          title="業務フロー図"
          documentType="workflow"
          createdAt={documentInfo.createdAt}
          modifiedAt={documentInfo.modifiedAt}
          {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
          isEditable={false}
          isLoading={isLoading}
          handleHistoryClick={handleHistoryClick}
        />
      )}

      {hasError ? (
        <p className={styles["errorMessage"]}>
          このデータは旧形式で作成されており、表示できません。
        </p>
      ) : (
        <div className={styles["diagramContainer"]}>
          <DrawioContainer
            xml={currentXml || ""}
            documentType="workflow"
            documentName={`業務フロー図_${currentProject?.name}`}
            onSave={handleSave}
          />
        </div>
      )}
    </div>
  );
};

export default WorkFlow;
